import { generateTextFromTipTapContent, tiptapContentSchema } from '../util';
import {
  ForumTagName,
  type Post,
  ForumType,
  AcceleratorForums,
  PublicForums,
  Forums,
  type ExtendedForumDataList,
} from '../lib/forums';
import { z } from 'zod';

export const forumInputSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(ForumType.PUBLIC),
    name: z.nativeEnum(PublicForums),
  }),
  z.object({
    type: z.literal(ForumType.ACCELERATOR),
    name: z.nativeEnum(AcceleratorForums),
  }),
]);

export const createPostSchema = z.object({
  title: z.string().min(1, { message: 'Title is required' }),
  forumName: z.nativeEnum(Forums, {
    required_error: 'Forum is required',
  }),
  forumType: z.nativeEnum(ForumType, {
    required_error: 'Forum type is required',
  }),
  tags: z
    .array(z.nativeEnum(ForumTagName))
    .max(3, { message: 'A post may only have up to 3 tags.' })
    .optional()
    .default([]),
  body: tiptapContentSchema.refine(
    (data) => {
      try {
        const text = generateTextFromTipTapContent(data);
        return text.length > 0;
      } catch {
        return false;
      }
    },
    {
      message: 'Post body is required',
      path: [],
    },
  ),
});

export type CreatePostBody = z.infer<typeof createPostSchema>;

export type CreatePostResponse = {
  postId: number;
  message: string;
};

export const retrievePostSchema = z.object({
  postId: z.coerce.number(),
});

export type RetrievePostQuery = z.infer<typeof retrievePostSchema>;

export type RetrievePostResponse = {
  post: Post;
};

export const ForumSortBy = {
  NEW: 'NEW',
  TRENDING: 'TRENDING',
} as const;

export const forumSortBySchema = z
  .nativeEnum(ForumSortBy)
  .optional()
  .default(ForumSortBy.NEW);

export type ForumSortBy = z.infer<typeof forumSortBySchema>;

export type ForumInput = z.infer<typeof forumInputSchema>;

export const listPostsSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('user'),
    username: z.string(),
    limit: z.coerce.number().optional(),
    offset: z.coerce.number().optional(),
  }),
  z.object({
    type: z.literal('forum'),
    forumType: z.nativeEnum(ForumType),
    forumName: z.nativeEnum(Forums).optional(),
    tags: z.array(z.nativeEnum(ForumTagName)).optional(),
    search: z.string().optional(),
    sortBy: forumSortBySchema,
    limit: z.coerce.number().optional(),
    offset: z.coerce.number().optional(),
  }),
]);

export type ForumListPostsQuery = Extract<
  z.infer<typeof listPostsSchema>,
  { type: 'forum' }
>;

export type UserListPostsQuery = Extract<
  z.infer<typeof listPostsSchema>,
  { type: 'user' }
>;

export type ListPostsQuery = z.infer<typeof listPostsSchema>;

export type PostSearchResult = {
  title: string;
  body: string;
};

export type PostWithSearchResult = {
  post: Post;
  searchResult?: PostSearchResult;
};

export interface ListPostsResponse {
  posts: PostWithSearchResult[];
  hasMore: boolean;
  offset: number;
}

export type RetrieveForumsResponse = {
  forums: ExtendedForumDataList;
};

export const deletePostSchema = z.object({
  postId: z.coerce.number(),
});

export type DeletePostQuery = z.infer<typeof deletePostSchema>;

export const editPostSchema = createPostSchema.extend({
  id: z.number(),
});

export type EditPostBody = z.infer<typeof editPostSchema>;
